import Phaser from 'phaser';
import type { FactionId } from '../../shared/types';

/**
 * BattleParallax — 戰鬥背景重畫(boss 2026-06-16「背景重畫好看一點、搭建築物/場景物體」
 * →「精緻一點、看別人怎麼做」)。
 *
 * 研究結論(itch.io / CraftPix / Phaser 教學):精緻 2D 側捲背景 = 「多層真實美術圖 +
 * 大氣霧層 + 不同速度視差」,純程序剪影不夠看。改用本機 ComfyUI(waiIllustrious)生的
 * 5 張 painterly 門派戰場 backdrop(public/assets/backgrounds/bg-scene-<faction>.jpg):
 *   星算=星空極光城 / 符文=發光魔法陣神殿 / 機械=霓虹雨夜城 / 自然=巨樹迷霧林 /
 *   疆域=燃燒要塞。
 *
 * 上層處理(全程序、可調):覆蓋整面 + 暗化(tint + 暗罩)保戰場可讀性 + 緩慢左右飄移
 * (景深感、無接縫)+ 門派色大氣粒子(星/燼)飄動。backdrop 缺檔 → 回退舊版程序剪影。
 *
 * 全 try-wrapped + headless-gated。所有層在實體(depth 0+)之下。
 */

/**
 * 一般波次:水平無縫全景橫條(連續天際線,可無限平鋪橫捲)。boss 2026-06-16:
 *  「我們是橫向的、你那種(構圖)圖比較像魔王關卡場景」→ 捲動層用無中心主體的全景條;
 *  「關卡場景風格應該一樣、多畫幾張隨機接續」→ 每門派 N 張「同風格不同景」的橫條
 *  (bg-scroll-<faction>-1..10.jpg),一關內隨機輪替(風格一致、景色變化)。
 */
const SCROLL_VARIANT_MAX = 10;

function scrollVariantKeys(faction: FactionId): string[] {
  const keys: string[] = [];
  for (let i = 1; i <= SCROLL_VARIANT_MAX; i++) keys.push(`bg-scroll-${faction}-${i}`);
  return keys;
}

/** 魔王戰定點場景(構圖式 painterly,正中有主體)。保留待 boss-arena 接線使用。 */
export const BATTLE_BACKDROP_KEYS: Record<FactionId, string> = {
  celestial: 'bg-scene-celestial',
  runic: 'bg-scene-runic',
  techno: 'bg-scene-techno',
  elemental: 'bg-scene-elemental',
  dominion: 'bg-scene-dominion',
};

/** Queue this stage's faction scroll variants on the loader. Call from scene preload(). */
export function preloadBattleBackdrops(scene: Phaser.Scene, faction: FactionId): void {
  for (const key of scrollVariantKeys(faction)) {
    try {
      if (!scene.textures?.exists?.(key)) scene.load.image(key, `assets/backgrounds/${key}.jpg`);
    } catch { /* headless;缺檔(未生成那麼多張)由 loaderror 靜默吞掉 */ }
  }
}

interface Palette { skyTop: number; skyHorizon: number; far: number; mid: number; near: number; window: number; particle: 'star' | 'ember'; }

const PALETTES: Record<FactionId, Palette> = {
  celestial: { skyTop: 0x080820, skyHorizon: 0x281c52, far: 0x140f30, mid: 0x1d1542, near: 0x0c0820, window: 0x9c6dff, particle: 'star' },
  runic:     { skyTop: 0x06121e, skyHorizon: 0x123444, far: 0x0d2230, mid: 0x123040, near: 0x081722, window: 0x4fc3f7, particle: 'star' },
  techno:    { skyTop: 0x05140f, skyHorizon: 0x0b3a30, far: 0x0a2420, mid: 0x0e3630, near: 0x06201c, window: 0x35e0c8, particle: 'star' },
  elemental: { skyTop: 0x0a1808, skyHorizon: 0x223a14, far: 0x0e2410, mid: 0x163a18, near: 0x0a1c0c, window: 0xaed581, particle: 'ember' },
  dominion:  { skyTop: 0x1a0d05, skyHorizon: 0x3a220e, far: 0x241408, mid: 0x36220c, near: 0x1a0f06, window: 0xffb74d, particle: 'ember' },
};

interface Layer { obj: Phaser.GameObjects.TileSprite; speed: number; }

let _texSeq = 0;

export class BattleParallax {
  private layers: Layer[] = [];
  private statics: Phaser.GameObjects.GameObject[] = [];
  private texKeys: string[] = [];
  private prefix = '';
  // 漸進變換場景(boss 2026-06-16「每一波漸進變換場景」):遠景捲動層 + 5 個 realm 輪替順序。
  private _far: Phaser.GameObjects.TileSprite | null = null;
  private _farKey = '';
  private _sceneKeys: string[] = [];

  constructor(private scene: Phaser.Scene) { this.prefix = `bpx${_texSeq++}`; }

  create(faction: FactionId): void {
    const pal = PALETTES[faction] ?? PALETTES.celestial;
    const W = this.scene.scale?.width ?? 800;
    const H = this.scene.scale?.height ?? 600;
    // 本關門派已載入的全景變體(同風格多張)。隨機開場 + 每波隨機接續。
    const variants = scrollVariantKeys(faction).filter((k) => this.scene.textures?.exists?.(k));

    if (variants.length > 0) {
      this._sceneKeys = variants;
      const first = variants[Math.floor(Math.random() * variants.length)];
      this.createScrollingBackdrop(first, W, H);
      try { this.drawParticles(W, Math.round(H * 0.55), pal); } catch { /* headless */ }
      return;
    }
    // ── fallback:backdrop 缺檔 → 程序剪影(天空 + 遠中近建築)──
    const horizon = Math.round(H * 0.62);
    try { this.drawSky(W, H, horizon, pal); } catch { /* headless */ }
    try { this.drawParticles(W, horizon, pal); } catch { /* headless */ }
    this.addBuildingLayer('far', pal.far, pal.window, { W, H, baseY: horizon + 8, minH: 28, maxH: 70, slotW: 64, windowChance: 0, depth: -9.5, speed: 0.12 });
    this.addBuildingLayer('mid', pal.mid, pal.window, { W, H, baseY: horizon + 30, minH: 50, maxH: 130, slotW: 78, windowChance: 0.85, depth: -9.3, speed: 0.4 });
    this.addBuildingLayer('near', pal.near, pal.window, { W, H, baseY: H + 6, minH: 36, maxH: 96, slotW: 104, windowChance: 0.55, depth: -9.1, speed: 0.9 });
  }

  /** 水平無縫全景橫條:覆蓋整面 + 橫向捲動(側捲前進感)+ 暗化保戰場可讀性。 */
  private createScrollingBackdrop(key: string, W: number, H: number): void {
    try {
      const src = this.scene.textures.get(key).getSourceImage() as { width?: number; height?: number };
      const ih = src.height || 600;
      const ts = this.scene.add.tileSprite(0, 0, W, H, key).setOrigin(0, 0).setDepth(-9.7).setScrollFactor(0);
      // tileScale:讓圖高對齊螢幕高;水平無縫 → 平鋪橫捲無限循環。
      const sc = H / ih;
      try { ts.setTileScale(sc, sc); } catch { /* headless */ }
      // 暗化(乘法 tint):壓背景亮度,讓前景子彈/敵人跳出來。
      try { ts.setTint(0x8890a4); } catch { /* headless */ }
      this.layers.push({ obj: ts, speed: 0.4 }); // 遠景慢捲(update() 每幀推 tilePositionX)
      this._far = ts; this._farKey = key;
      // 暗罩 + 底部地面加深 → 進一步抬前景可讀性。
      const veil = this.scene.add.rectangle(0, 0, W, H, 0x05060f, 0.26).setOrigin(0, 0).setDepth(-9.5).setScrollFactor(0);
      this.statics.push(veil);
      const floor = this.scene.add.rectangle(0, H, W, H * 0.3, 0x03040a, 0.42).setOrigin(0, 1).setDepth(-9.45).setScrollFactor(0);
      this.statics.push(floor);
    } catch { /* headless / texture race */ }
  }

  private drawSky(W: number, H: number, horizon: number, pal: Palette): void {
    const g = this.scene.add.graphics().setDepth(-9.7).setScrollFactor(0);
    const top = Phaser.Display.Color.ValueToColor(pal.skyTop);
    const bot = Phaser.Display.Color.ValueToColor(pal.skyHorizon);
    const steps = 48;
    for (let i = 0; i < steps; i++) {
      const t = i / (steps - 1);
      const c = Phaser.Display.Color.Interpolate.ColorWithColor(top, bot, 100, t * 100);
      g.fillStyle(Phaser.Display.Color.GetColor(c.r, c.g, c.b), 1);
      g.fillRect(0, (horizon / steps) * i, W, horizon / steps + 1);
    }
    g.fillStyle(pal.skyHorizon, 0.5);
    g.fillRect(0, horizon - 24, W, 48);
    g.fillStyle(pal.skyTop, 1);
    g.fillRect(0, horizon, W, H - horizon);
    this.statics.push(g);
  }

  private drawParticles(W: number, horizon: number, pal: Palette): void {
    const rnd = new Phaser.Math.RandomDataGenerator([this.prefix]);
    if (pal.particle === 'star') {
      for (let i = 0; i < 60; i++) {
        const x = rnd.between(0, W);
        const y = rnd.between(0, Math.max(20, horizon - 40));
        const r = rnd.realInRange(0.5, 1.5);
        const s = this.scene.add.circle(x, y, r, 0xffffff, rnd.realInRange(0.25, 0.8)).setDepth(-9.4).setScrollFactor(0);
        this.statics.push(s);
        if (rnd.frac() < 0.4) this.scene.tweens.add({ targets: s, alpha: 0.08, duration: rnd.between(900, 2300), yoyo: true, repeat: -1, delay: rnd.between(0, 1500) });
      }
    } else {
      for (let i = 0; i < 24; i++) {
        const x = rnd.between(0, W);
        const y = rnd.between(horizon - 60, horizon + 120);
        const s = this.scene.add.circle(x, y, rnd.realInRange(1, 2.2), pal.window, rnd.realInRange(0.35, 0.8)).setDepth(-9.4).setScrollFactor(0);
        try { s.setBlendMode(Phaser.BlendModes.ADD); } catch { /* headless */ }
        this.statics.push(s);
        this.scene.tweens.add({
          targets: s, y: y - rnd.between(100, 240), alpha: 0, duration: rnd.between(2400, 4600), repeat: -1, delay: rnd.between(0, 2800),
          onRepeat: () => { try { s.setAlpha(rnd.realInRange(0.35, 0.8)); s.y = y; } catch { /* noop */ } },
        });
      }
    }
  }

  private addBuildingLayer(
    name: string, color: number, windowColor: number,
    o: { W: number; H: number; baseY: number; minH: number; maxH: number; slotW: number; windowChance: number; depth: number; speed: number },
  ): void {
    const key = `${this.prefix}-${name}`;
    try {
      if (!this.scene.textures.exists(key)) {
        const g = this.scene.make.graphics({ x: 0, y: 0 }, false);
        const rnd = new Phaser.Math.RandomDataGenerator([key]);
        for (let x = 18; x < o.W - 18; x += o.slotW) {
          const bw = o.slotW - rnd.between(10, 26);
          const bh = rnd.between(o.minH, o.maxH);
          const bx = x + rnd.between(0, 8);
          const by = o.baseY - bh;
          g.fillStyle(color, 1);
          g.fillRect(bx, by, bw, o.baseY - by);
          const top = rnd.frac();
          if (top < 0.33) g.fillTriangle(bx - 3, by, bx + bw + 3, by, bx + bw / 2, by - rnd.between(14, 30));
          else if (top < 0.66) { for (let i = 0; i < bw; i += 12) g.fillRect(bx + i, by - 8, 7, 9); }
          else g.fillRect(bx + bw / 2 - 1, by - rnd.between(12, 26), 2, 26);
          if (rnd.frac() < o.windowChance) {
            g.fillStyle(windowColor, 0.5);
            for (let wy = by + 8; wy < o.baseY - 6; wy += 12) for (let wx = bx + 5; wx < bx + bw - 5; wx += 12) if (rnd.frac() < 0.55) g.fillRect(wx, wy, 4, 5);
          }
        }
        g.generateTexture(key, o.W, o.H);
        g.destroy();
        this.texKeys.push(key);
      }
      const ts = this.scene.add.tileSprite(0, 0, o.W, o.H, key).setOrigin(0, 0).setDepth(o.depth).setScrollFactor(0);
      this.layers.push({ obj: ts, speed: o.speed });
    } catch { /* headless */ }
  }

  /**
   * boss 2026-06-16「每一波漸進變換場景」:crossfade 遠景捲動橫條到下一個門派 realm。
   * 由戰鬥場景在 _beginNextWave 帶 currentWave 呼叫;從本關門派起依序輪替。新層淡入、舊層
   * 淡出後銷毀,捲動位置對齊不跳。只在「捲動橫條」模式可用(程序剪影 fallback 不換)。
   */
  setSceneForWave(_waveIndex: number): void {
    try {
      if (this._sceneKeys.length < 2) return; // 只有一張就沒得換
      // 隨機接續:從同門派變體中挑一張(排除目前這張),風格一致、景色變化。
      const choices = this._sceneKeys.filter((k) => k !== this._farKey);
      if (choices.length === 0) return;
      const key = choices[Math.floor(Math.random() * choices.length)];
      if (!this.scene.textures?.exists?.(key)) return;
      const W = this.scene.scale?.width ?? 800;
      const H = this.scene.scale?.height ?? 600;
      const src = this.scene.textures.get(key).getSourceImage() as { height?: number };
      const ih = src.height || 600;
      const ts = this.scene.add.tileSprite(0, 0, W, H, key).setOrigin(0, 0).setDepth(-9.68).setScrollFactor(0).setAlpha(0);
      try { ts.setTileScale(H / ih, H / ih); } catch { /* headless */ }
      try { ts.setTint(0x8890a4); } catch { /* headless */ }
      if (this._far) { try { ts.tilePositionX = this._far.tilePositionX; } catch { /* noop */ } }
      this.layers.push({ obj: ts, speed: 0.4 });
      const old = this._far;
      this._far = ts; this._farKey = key;
      this.scene.tweens.add({
        targets: ts, alpha: 1, duration: 1600, ease: 'Sine.easeInOut',
        onComplete: () => {
          if (old) { this.layers = this.layers.filter((l) => l.obj !== old); try { old.destroy(); } catch { /* noop */ } }
        },
      });
    } catch { /* headless */ }
  }

  update(): void {
    for (const l of this.layers) { try { l.obj.tilePositionX += l.speed; } catch { /* headless */ } }
  }

  destroy(): void {
    for (const l of this.layers) { try { l.obj.destroy(); } catch { /* noop */ } }
    for (const s of this.statics) { try { s.destroy(); } catch { /* noop */ } }
    for (const k of this.texKeys) { try { this.scene.textures.remove(k); } catch { /* noop */ } }
    this.layers = []; this.statics = []; this.texKeys = [];
  }
}
